import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { State as OverviewState } from '../reducers/overview';
import { State as LabelState } from '../../soft-labels/reducers/soft-labels';
import { navEnabled, ButtonWidget } from '../models/button-widget';
import { OverviewUrlService } from '@feature/overview/services/overview-url.service';
import {DashboardIdentifiers} from '@dashboards/dashboard-identifiers';
import { IOverviewConfigurationModel } from '../models/overview-configuration-model';
import * as userSettingsSelectors from '../../user-settings/selectors/user-settings';
import { RouteContextItemType } from '@context/old_index';
import { OverviewItemDefinition } from './overview-item-definition';
import { UserPreferencesData } from '@feature/user-preferences/models/user-preferences-data';
import { getUserPreferences } from '@feature/user-preferences/selectors/user-preferences';
import {removeListerPageRouteFromUrl} from "@routing/lister-page-paths";
import {MojoWellKnownNames} from "@feature/common";

export const selectOverviewState = createFeatureSelector<OverviewState>('overview');
export const selectLabelState = createFeatureSelector<LabelState>('softLabels');

export function getOverviewTiles(url: string, routeContextItemType: RouteContextItemType) {
  return createSelector(
    [selectOverviewState,
     selectLabelState,
     getOverviewConfiguration(),
     getUserPreferences(),
     userSettingsSelectors.getIsMyDashboardMode(),
     userSettingsSelectors.getPreviewMode],
    (state: OverviewState,
     labels: LabelState,
     config: IOverviewConfigurationModel,
     effectiveValues: UserPreferencesData,
     isInMyDashboardMode: boolean,
     isInPreviewMode: boolean) => {
      const items: ButtonWidget[] = [];
      const readyForOverview =
        !!routeContextItemType
        && !!labels.entitySoftlabels
        && !!config
        && !!state.overview
        && !!effectiveValues;
      if (!readyForOverview)
        return items;

      const urlService = new OverviewUrlService();
      const hasDashboard = (id: string) => config.AvailableTiles.find(item => item.Name === id) != undefined;
      const skipListerPage = effectiveValues.SkipListerPage;
      const getOverviewCountForEntityType = (et) => state.overview.Counts[et] || 0;
      const getOverviewKeyForEntityType = (et) => state.overview.Keys[et];
      const isMojoDashboardOnly = (s) => hasDashboard(s) && isInMyDashboardMode && !isInPreviewMode;
      const btn = (entityName: string, plural: string, softLabel: string) => {
        const b = new ButtonWidget(softLabel,
          true,
          getOverviewCountForEntityType(entityName),
          null,
          getOverviewButtonUrl(entityName, plural),
          routeContextItemType.toLocaleLowerCase() === entityName.toLocaleLowerCase());
          if (b.number < 0) {
            b.showNumber = false;
          }
          return b;
      };

      const getOverviewButtonUrl = (entityName, listerPlural) => {
        const dashboardKey = getOverviewKeyForEntityType(entityName);
        const dashboardUrl = dashboardKey
          ? `${urlService.getDashboardUrlFromEntityKey(MojoWellKnownNames.Products.MojoDashboard, entityName, dashboardKey)}`
          : undefined;
        const currentEntityUrl = removeListerPageRouteFromUrl(url);
        const listerUrl = !currentEntityUrl
          ? `/entity-data/${listerPlural}`
          : `${currentEntityUrl}/${listerPlural}`;
        return (dashboardKey && skipListerPage) || currentEntityUrl == dashboardUrl ? dashboardUrl : listerUrl;
      };

      const potentialOverviewItems: OverviewItemDefinition[] = [
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.ManagerDashboard),
          buttonFactory: () => btn('Manager', 'managers', labels.entitySoftlabels.ManagersLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.ClientParentDashboard),
          buttonFactory: () => btn('ClientParent', 'client-parents', labels.entitySoftlabels.ClientParentsLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.ClientDashboard),
          buttonFactory: () => btn('Client', 'clients', labels.entitySoftlabels.ClientsLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.EstateDashboard),
          buttonFactory: () => btn('Estate', 'estates', labels.entitySoftlabels.EstatesLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.PropertyDashboard),
          buttonFactory: () => btn('Property', 'properties', labels.entitySoftlabels.PropertiesLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.HeadleaseDashboard),
          buttonFactory: () => btn('HeadLease', 'headleases', labels.entitySoftlabels.HeadLeasesLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.LandlordDashboard),
          buttonFactory: () => btn('Landlord', 'landlords', labels.entitySoftlabels.LandlordsLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.UnitDashboard),
          buttonFactory: () => btn('Unit', 'units', labels.entitySoftlabels.UnitsLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.TenantParentDashboard),
          buttonFactory: () => btn('TenantParent', 'tenant-parents', labels.entitySoftlabels.TenantParentsLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.TenantDashboard),
          buttonFactory: () => btn('Tenant', 'tenants', labels.entitySoftlabels.TenantsLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.LeaseDashboard),
          buttonFactory: () => btn('Lease', 'leases', labels.entitySoftlabels.LeasesLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.SupplierDashboard),
          buttonFactory: () => btn('Supplier', 'suppliers', labels.entitySoftlabels.SuppliersLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.DebtorDashboard),
          buttonFactory: () => btn('Debtor', 'debtors', labels.entitySoftlabels.DebtorsLabel)
        },
        {
          addItemPredicate: () => isMojoDashboardOnly(DashboardIdentifiers.CreditorDashboard),
          buttonFactory: () => btn('Creditor', 'creditors', labels.entitySoftlabels.CreditorsLabel)
        },
        {
          addItemPredicate: () => hasDashboard(DashboardIdentifiers.VacantDashboard),
          buttonFactory: () => btn('Vacant', 'vacants', labels.entitySoftlabels.VacantsLabel)
        },
      ];

      items.push(new ButtonWidget('Home', false, 0, null, '/entity-data/main', routeContextItemType === 'None', null, navEnabled));
      for (let i = 0; i < potentialOverviewItems.length; i++) {
        if (potentialOverviewItems[i].addItemPredicate()) {
          items.push(potentialOverviewItems[i].buttonFactory());
        }
      }

      return items;
    }
  );
}

export function getOverviewConfiguration() {
  return createSelector([selectOverviewState], (overview: OverviewState) => overview.configuration);
}
